import { useTranslation } from "react-i18next"

import { CrewPulseEmpty } from "@/components/creator/crew-pulse-empty"
import { PulseRow } from "@/components/creator/pulse-row"
import { usePulseStream } from "@/hooks/use-pulse-stream"
import { STATUS_AFFORDANCES } from "@/lib/motion"
import { cn } from "@/lib/utils"
import type { CrewPulseEvent } from "@/types/collab"

interface CrewPulseStreamProps {
  events: CrewPulseEvent[]
}

/**
 * OKS-473 — Crew Pulse FULL STREAM (UX Spec §1.2 / §2.2).
 *
 * Renders the typed-event list with live-arrival handling driven by
 * `usePulseStream`: when new events land while the user is scrolled down, a
 * centred "N new — tap to see" pill appears instead of jumping the scroll;
 * tapping it prepends the events (one-shot beat) and scrolls to top. At
 * scroll-top, arrivals prepend silently with the beat.
 *
 * The list is an `aria-live="polite"` region so arrivals are announced; the
 * pill is a real `<button>` (Enter/Space). Focus is intentionally NOT yanked to
 * the top on reveal (UX Spec §6 — only the scroll position moves).
 */
export function CrewPulseStream({ events }: CrewPulseStreamProps) {
  const { t } = useTranslation()
  const { displayed, newIds, pendingCount, reveal } = usePulseStream(events)

  if (displayed.length === 0) {
    return <CrewPulseEmpty />
  }

  return (
    <div className="relative flex flex-col">
      {pendingCount > 0 ? (
        <div className="pointer-events-none sticky top-2 z-10 flex justify-center">
          <button
            type="button"
            onClick={reveal}
            data-testid="crew-pulse-new-pill"
            className="bg-primary text-primary-foreground pointer-events-auto inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium shadow-md"
          >
            <span
              aria-hidden="true"
              className={cn(
                "size-2 rounded-full bg-current opacity-90",
                // OKS-562 — the "N new" pill dot is a CONTINUOUS attention cue
                // (status affordance), not a one-shot arrival beat. Uses
                // `live-status` (the breathing dot), NOT the now single-entrance
                // `live-pulse`. motion-safe → static under reduced-motion.
                STATUS_AFFORDANCES["live-status"],
              )}
            />
            {t("crew_pulse.new_pill", { count: pendingCount })}
          </button>
        </div>
      ) : null}

      <ul
        data-testid="crew-pulse-stream-list"
        aria-live="polite"
        aria-label={t("crew_pulse.region_label")}
        className="divide-border/60 flex flex-col divide-y"
      >
        {displayed.map((event) => (
          <PulseRow key={event.id} event={event} isNew={newIds.has(event.id)} />
        ))}
      </ul>
    </div>
  )
}
